import { useState, ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Modal,
  Input,
  Loader,
  styled,
  TextInput,
  SelectionStatus,
} from "@monolith-forensics/monolith-ui";
import CasesApi from "../../../api/cases";
import { useDebounce } from "use-debounce";
import EvidenceCocAPI from "../../../api/evidence/coc";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { Case } from "@/Features/Cases";
import { GetEvidenceQuery } from "../evidence.api.types";

type SelectionState = {
  caseInfo: {
    case_id: number;
    uuid: string;
    case_number: string;
    case_ref: string;
  };
  options: {
    selectionStatus: SelectionStatus;
    selectedItems: string[];
    excludedItems: string[];
    totalSelected: number;
    query?: GetEvidenceQuery;
  };
  total: number;
};

// Component prop interfaces
type CaseResultItemProps = {
  className?: string;
  item: Case;
  onSelect?: (item: Case) => void;
  selectedItem?: Case | null;
};

type CaseResultsProps = {
  className?: string;
  data?: Case[];
  onSelect: (item: Case) => void;
  selectedItem: Case | null;
};

type CasesSearchProps = {
  className?: string;
  onSelect: (item: Case) => void;
  selectedItem: Case | null;
  caseInfo: Partial<Case>;
};

type MigrateEvidenceModalProps = {
  className?: string;
  open: boolean;
  onSubmit?: () => void;
  handleClose?: () => void;
  selectionState?: SelectionState;
  caseInfo?: Partial<Case>;
};

const StyledContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;

  .selection-result {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin: 0 auto;

    .case-result-item:hover {
      border: 1px solid ${(props) => props.theme.palette.divider};
      cursor: default;
    }
  }

  .error-message {
    font-size: 12px;
    font-weight: 600;
    color: ${(props) => props.theme.palette.error.main};
  }

  .migrate-segment {
    display: flex;
    flex-direction: row;
    gap: 5px;
    align-items: center;
    width: 100%;

    .direction {
      font-size: 12px;
      font-weight: 600;
      color: ${(props) => props.theme.palette.text.secondary};
      min-width: 50px;
    }
  }

  .thick {
    font-weight: 600;
  }

  .subtle {
    color: ${(props) => props.theme.palette.text.secondary};
  }

  .item-total {
    font-size: 32px;
    font-weight: 600;
    color: ${(props) => props.theme.palette.primary.main};
  }

  .case-number {
    font-weight: 600;
    color: ${(props) => props.theme.palette.primary.main};
  }
`;

const CaseResultItem = styled(
  ({ className, item, onSelect, selectedItem = null }: CaseResultItemProps) => {
    return (
      <div
        data-selected={selectedItem?.uuid === item?.uuid}
        className={className + " case-result-item"}
        onClick={() => onSelect?.(item)}
      >
        <div className="case-number">{item.case_number}</div>
        <div className="case-name">{item.case_ref}</div>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  padding: 10px;
  height: auto;
  width: 300px;
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 5px;
  cursor: pointer;

  transition: border 0.1s ease-in-out;

  &:hover {
    border: 1px solid ${(props) => props.theme.palette.primary.main};
  }

  .case-number {
    font-size: 12px;
    font-weight: 600;
    color: ${(props) => props.theme.palette.text.primary};
  }

  .case-name {
    font-size: 10px;
    color: ${(props) => props.theme.palette.text.secondary};
    max-width: 90%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CaseResults = styled(
  ({ className, data, onSelect, selectedItem }: CaseResultsProps) => {
    return (
      <div className={className}>
        <div className="title">Select Case for Migration</div>
        <div className="results-container">
          {data?.length === 0 && <div>No Results</div>}
          {data?.map((item: Case) => (
            <CaseResultItem
              key={item.uuid || item.case_id}
              item={item}
              onSelect={onSelect}
              selectedItem={selectedItem}
            />
          ))}
        </div>
      </div>
    );
  }
)`
  user-select: none;
  display: flex;
  flex-direction: column;
  gap: 5px;

  .results-container {
    display: flex;
    flex-direction: column;
    gap: 5px;
    max-height: 300px;
    overflow-y: auto;
    padding-right: 5px;

    margin-top: 10px;
    margin-bottom: 10px;
    margin-left: auto;
    margin-right: auto;
  }

  .title {
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    color: ${(props) => props.theme.palette.text.secondary};
  }
`;

const CasesSearch = styled(
  ({ className, onSelect, selectedItem, caseInfo }: CasesSearchProps) => {
    const [search, setSearch] = useState("");
    const [value] = useDebounce(search, 350);

    const { data, isFetching } = useQuery({
      queryKey: ["cases", "search", { search: value }],
      queryFn: () => CasesApi.getCases({ search: value }),
      enabled: value.length > 3,
    });

    return (
      <div className={className}>
        <Input
          placeholder="Search for Case"
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSearch(e.target.value)
          }
          style={{ marginTop: 10 }}
          autoFocus
        />
        {!data && isFetching && (
          <div>
            <Loader />
          </div>
        )}
        {value.length > 3 && (
          <>
            <CaseResults
              data={data?.filter((item: Case) => item.uuid !== caseInfo?.uuid)}
              onSelect={onSelect}
              selectedItem={selectedItem}
            />
          </>
        )}
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 auto;
  max-width: 300px;
  width: 300px;
`;

export const MigrateEvidenceModal = ({
  open,
  onSubmit = () => {},
  handleClose = () => {},
  selectionState,
  caseInfo,
}: MigrateEvidenceModalProps) => {
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmTotal, setConfirmTotal] = useState<string>("");
  const [error, setError] = useState<string>();

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      if (!selectedCase) {
        setError("Please select a case to migrate evidence to");
        setSubmitting(false);
        return;
      }
      if (confirmTotal !== selectionState?.total.toString()) {
        setError("Total is not correct");
        setSubmitting(false);
        return;
      }
      await EvidenceCocAPI.migrateItems({
        migrate_to: {
          case_id: selectedCase.case_id,
          case_uuid: selectedCase.uuid,
          case_number: selectedCase.case_number,
        },
        selectionState: selectionState.options,
        confirmTotal: Number(confirmTotal),
      });
      setError(undefined);
      onSubmit?.();
      handleClose();
    } catch (err: any) {
      setError(err.message || "An error occurred during migration");
    } finally {
      setSelectedCase(null);
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedCase(null);
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Modal.Title>Migrate Evidence</Modal.Title>
      <StyledContainer>
        {submitting ? (
          <Loader />
        ) : (
          <>
            <div className="subtle">
              Migrate the selected evidence items from this case to another
              case.
            </div>
            {error && <div className="error-message">{error}</div>}
            {!selectedCase && (
              <div className="thick" style={{ textAlign: "center" }}>
                <div className="item-total">{selectionState?.total || 0}</div>
                <div>Evidence Items will be migrated.</div>
              </div>
            )}
            {!selectedCase && ( // hide search if case is selected
              <CasesSearch
                onSelect={setSelectedCase}
                selectedItem={selectedCase}
                caseInfo={caseInfo || {}}
              />
            )}
            {selectedCase && (
              <div className="selection-result">
                <div className="thick" style={{ textAlign: "center" }}>
                  <div className="item-total">{selectionState?.total || 0}</div>
                  <div>Evidence Items will be migrated.</div>
                </div>
                <div className="migrate-segment">
                  <div className="direction">FROM</div>
                  {caseInfo && <CaseResultItem item={caseInfo as Case} />}
                </div>
                <div className="migrate-segment">
                  <div className="direction">TO</div>
                  {selectedCase && <CaseResultItem item={selectedCase} />}
                </div>
                <TextInput
                  placeholder={`${selectionState?.total || 0}`}
                  label="Enter Total to Confirm Migration"
                  required={true}
                  style={{ width: "100%" }}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setConfirmTotal(e.target.value)
                  }
                  error={error}
                />
              </div>
            )}
          </>
        )}
        <ButtonMenu>
          <Button
            size="xs"
            variant="subtle"
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            loading={submitting}
          >
            Migrate
          </Button>
        </ButtonMenu>
      </StyledContainer>
    </Modal>
  );
};
